import { authedFetch } from '../lib/authedFetch';
import { REPORT_PDF_BUILDER_VERSION, SUMMARY_BUILDER_VERSION } from '../lib/screeningSummary/vocabulary';
import type { ReportArtifactDeliveryNotification } from '../types/reportArtifactDeliveryNotification';

export type ReportArtifactFormat = 'pdf' | 'docx' | 'pptx' | 'csv';
export type ReportArtifactPageSize = 'A4' | 'Letter';
export type ReportArtifactSourceKind =
  | 'report'
  | 'analysis'
  | 'report-content'
  | 'decision-dossier';
export type ReportArtifactJobStatus = 'queued' | 'running' | 'succeeded' | 'failed';

export interface ReportArtifactAnalysisIdentity {
  analysisId: string;
  inputHash: string;
  contentHash: string;
}

type ReportArtifactFormatRequest =
  | { format: 'pdf'; pageSize: ReportArtifactPageSize }
  | { format: Exclude<ReportArtifactFormat, 'pdf'>; pageSize?: never };

type ReportArtifactRequestBase = {
  schemaVersion: 1;
  reportId: string;
  reportRevisionId: string;
  view?: 'screening-summary' | 'report-summary';
  summaryBuilderVersion?: string;
  pdfBuilderVersion?: string;
};

export type ReportArtifactJobRequest =
  | (ReportArtifactRequestBase & ReportArtifactFormatRequest & {
      sourceKind?: 'report';
    })
  | (ReportArtifactRequestBase & ReportArtifactFormatRequest & {
      sourceKind: 'analysis';
      analysisId: string;
      analysisInputHash: string;
      analysisContentHash: string;
    })
  | (ReportArtifactRequestBase & ReportArtifactFormatRequest & {
      sourceKind: 'report-content';
      reportContentRevisionId: string;
      reportContentInputHash: string;
      reportContentHash: string;
    })
  | (ReportArtifactRequestBase & ReportArtifactFormatRequest & {
      sourceKind: 'decision-dossier';
      analysisRevisions: ReportArtifactAnalysisIdentity[];
    });

export interface ReportArtifactJobView {
  deliveryNotification?: ReportArtifactDeliveryNotification;
  jobId: string;
  reportId: string;
  reportRevisionId: string;
  sourceKind: ReportArtifactSourceKind;
  analysisId?: string;
  analysisInputHash?: string;
  analysisContentHash?: string;
  reportContentRevisionId?: string;
  reportContentInputHash?: string;
  reportContentHash?: string;
  analysisRevisions?: ReportArtifactAnalysisIdentity[];
  format: ReportArtifactFormat;
  pageSize?: ReportArtifactPageSize;
  view?: 'screening-summary' | 'report-summary';
  summaryBuilderVersion?: string;
  pdfBuilderVersion?: string;
  requestHash: string;
  status: ReportArtifactJobStatus;
  attempt: number;
  progress: number;
  artifactState: 'pending' | 'staged' | 'ready' | 'failed';
  createdAt: string;
  updatedAt: string;
  errorCode?: string;
  error?: string;
  contentUrl?: string;
  artifact?: {
    format: ReportArtifactFormat;
    sha256: string;
    mediaType: string;
    byteLength: number;
    layoutStatus: 'pending' | 'valid' | 'invalid' | 'not-applicable';
    pageCount?: number;
    slideCount?: number;
  };
}

export interface BuildReportArtifactOptions {
  /** Explicit platform-admin recovery of another owner's known failed PDF.
   * Verified server-side; never changes the immutable request/source identity. */
  adminReportOwnerUid?: string;
  /** Exact shared PDF; server rechecks persisted report/site read access. */
  reportOwnerUid?: string;
  signal?: AbortSignal;
  timeoutMs?: number;
  pollIntervalMs?: number;
  onProgress?: (job: ReportArtifactJobView) => void;
  /** Dependency seam for deterministic browser-unit tests. */
  fetcher?: typeof authedFetch;
  now?: () => number;
}

export interface BuiltReportArtifact {
  job: ReportArtifactJobView;
  blob: Blob;
}

const ROUTE_BASE = '/api/report-artifacts/jobs';
const DEFAULT_TIMEOUT_MS = 15 * 60_000;
const DEFAULT_POLL_INTERVAL_MS = 1_500;
const SHA256 = /^[a-f0-9]{64}$/;
const STATUSES = new Set<ReportArtifactJobStatus>(['queued', 'running', 'succeeded', 'failed']);
const FORMATS = new Set<ReportArtifactFormat>(['pdf', 'docx', 'pptx', 'csv']);
const SOURCE_KINDS = new Set<ReportArtifactSourceKind>([
  'report',
  'analysis',
  'report-content',
  'decision-dossier',
]);
const PAGE_SIZES = new Set<ReportArtifactPageSize>(['A4', 'Letter']);
const ARTIFACT_STATES = new Set(['pending', 'staged', 'ready', 'failed']);
const LAYOUT_STATES = new Set(['pending', 'valid', 'invalid', 'not-applicable']);

function record(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

type ReportArtifactSourceIdentity = Pick<ReportArtifactJobView,
  'analysisId' | 'analysisInputHash' | 'analysisContentHash'
  | 'reportContentRevisionId' | 'reportContentInputHash' | 'reportContentHash'
  | 'analysisRevisions'>;

function parseSourceIdentity(
  candidate: Record<string, unknown>,
  sourceKind: ReportArtifactSourceKind,
): ReportArtifactSourceIdentity {
  const hash = (value: unknown): value is string => typeof value === 'string' && SHA256.test(value);
  if (sourceKind === 'analysis') {
    if (!optionalString(candidate.analysisId) || !hash(candidate.analysisInputHash) || !hash(candidate.analysisContentHash)) {
      throw new Error('The report artifact service returned an invalid analysis identity.');
    }
    return { analysisId: candidate.analysisId as string, analysisInputHash: candidate.analysisInputHash,
      analysisContentHash: candidate.analysisContentHash };
  }
  if (sourceKind === 'report-content') {
    if (!optionalString(candidate.reportContentRevisionId) || !hash(candidate.reportContentInputHash) || !hash(candidate.reportContentHash)) {
      throw new Error('The report artifact service returned an invalid report content identity.');
    }
    return { reportContentRevisionId: candidate.reportContentRevisionId as string,
      reportContentInputHash: candidate.reportContentInputHash, reportContentHash: candidate.reportContentHash };
  }
  if (sourceKind === 'decision-dossier') {
    if (!Array.isArray(candidate.analysisRevisions) || candidate.analysisRevisions.length === 0 || candidate.analysisRevisions.length > 32) {
      throw new Error('The report artifact service returned an invalid dossier identity.');
    }
    const ids = new Set<string>();
    const analysisRevisions = candidate.analysisRevisions.map((value): ReportArtifactAnalysisIdentity => {
      const reference = record(value);
      const analysisId = optionalString(reference?.analysisId);
      if (!reference || !analysisId || ids.has(analysisId) || !hash(reference.inputHash) || !hash(reference.contentHash)) {
        throw new Error('The report artifact service returned an invalid dossier identity.');
      }
      ids.add(analysisId);
      return { analysisId, inputHash: reference.inputHash, contentHash: reference.contentHash };
    });
    return { analysisRevisions };
  }
  return {};
}

function sameSourceIdentity(identity: ReportArtifactSourceIdentity, request: ReportArtifactJobRequest): boolean {
  if (request.sourceKind === 'analysis') return identity.analysisId === request.analysisId
    && identity.analysisInputHash === request.analysisInputHash && identity.analysisContentHash === request.analysisContentHash;
  if (request.sourceKind === 'report-content') return identity.reportContentRevisionId === request.reportContentRevisionId
    && identity.reportContentInputHash === request.reportContentInputHash && identity.reportContentHash === request.reportContentHash;
  if (request.sourceKind === 'decision-dossier') return Array.isArray(identity.analysisRevisions)
    && identity.analysisRevisions.length === request.analysisRevisions.length
    && identity.analysisRevisions.every((reference, index) => {
      const expected = request.analysisRevisions[index];
      return expected !== undefined && reference.analysisId === expected.analysisId
        && reference.inputHash === expected.inputHash && reference.contentHash === expected.contentHash;
    });
  return true;
}

function parseArtifact(value: unknown): ReportArtifactJobView['artifact'] {
  if (value === undefined) return undefined;
  const candidate = record(value);
  if (
    !candidate
    || typeof candidate.format !== 'string'
    || !FORMATS.has(candidate.format as ReportArtifactFormat)
    || typeof candidate.sha256 !== 'string'
    || !SHA256.test(candidate.sha256)
    || typeof candidate.mediaType !== 'string'
    || !candidate.mediaType
    || !Number.isSafeInteger(candidate.byteLength)
    || Number(candidate.byteLength) <= 0
    || typeof candidate.layoutStatus !== 'string'
    || !LAYOUT_STATES.has(candidate.layoutStatus)
  ) {
    throw new Error('The report artifact service returned an invalid artifact pointer.');
  }
  return {
    format: candidate.format as ReportArtifactFormat,
    sha256: candidate.sha256,
    mediaType: candidate.mediaType,
    byteLength: Number(candidate.byteLength),
    layoutStatus: candidate.layoutStatus as NonNullable<ReportArtifactJobView['artifact']>['layoutStatus'],
    ...(Number.isSafeInteger(candidate.pageCount) && Number(candidate.pageCount) > 0
      ? { pageCount: Number(candidate.pageCount) }
      : {}),
    ...(Number.isSafeInteger(candidate.slideCount) && Number(candidate.slideCount) > 0
      ? { slideCount: Number(candidate.slideCount) }
      : {}),
  };
}

function parseJob(value: unknown, request?: ReportArtifactJobRequest): ReportArtifactJobView {
  const candidate = record(value);
  if (
    !candidate
    || typeof candidate.jobId !== 'string'
    || !/^artifact-(?:pdf-(?:summary-)?(?:a4|letter)|docx|pptx|csv)-[a-f0-9]{40}$/.test(candidate.jobId)
    || typeof candidate.reportId !== 'string'
    || typeof candidate.reportRevisionId !== 'string'
    || typeof candidate.format !== 'string'
    || !FORMATS.has(candidate.format as ReportArtifactFormat)
    || typeof candidate.requestHash !== 'string'
    || !SHA256.test(candidate.requestHash)
    || typeof candidate.status !== 'string'
    || !STATUSES.has(candidate.status as ReportArtifactJobStatus)
    || !Number.isSafeInteger(candidate.attempt)
    || Number(candidate.attempt) < 0
    || !Number.isSafeInteger(candidate.progress)
    || Number(candidate.progress) < 0
    || Number(candidate.progress) > 100
    || typeof candidate.artifactState !== 'string'
    || !ARTIFACT_STATES.has(candidate.artifactState)
    || typeof candidate.createdAt !== 'string'
    || typeof candidate.updatedAt !== 'string'
  ) {
    throw new Error('The report artifact service returned an invalid job contract.');
  }
  const sourceKind = candidate.sourceKind === undefined
    ? 'report'
    : candidate.sourceKind;
  if (
    typeof sourceKind !== 'string'
    || !SOURCE_KINDS.has(sourceKind as ReportArtifactSourceKind)
    || (
      candidate.format === 'pdf'
      && (
        typeof candidate.pageSize !== 'string'
        || !PAGE_SIZES.has(candidate.pageSize as ReportArtifactPageSize)
      )
    )
    || (candidate.format !== 'pdf' && candidate.pageSize !== undefined)
    || (candidate.view !== undefined && candidate.view !== 'screening-summary' && candidate.view !== 'report-summary')
    || (candidate.view !== undefined && (candidate.format !== 'pdf' || (candidate.view === 'screening-summary' && sourceKind !== 'report')))
    || (candidate.summaryBuilderVersion !== undefined && (!candidate.view || !['1.2.0','1.3.0','1.3.1','1.4.0','1.5.0','1.5.1','1.6.0','1.6.1',SUMMARY_BUILDER_VERSION,'1.0.0'].includes(String(candidate.summaryBuilderVersion))))
    || (candidate.pdfBuilderVersion !== undefined && (candidate.format !== 'pdf' || candidate.view !== undefined || !['1.0.0', REPORT_PDF_BUILDER_VERSION].includes(String(candidate.pdfBuilderVersion))))
    || (candidate.view === 'report-summary' && candidate.summaryBuilderVersion !== '1.0.0')
    || (candidate.view === 'screening-summary' && candidate.summaryBuilderVersion !== undefined && !['1.2.0', '1.3.0', '1.3.1', '1.4.0', '1.5.0', '1.5.1', '1.6.0', '1.6.1', SUMMARY_BUILDER_VERSION].includes(String(candidate.summaryBuilderVersion)))
    || (candidate.jobId.includes('pdf-summary-') !== Boolean(candidate.view))
  ) {
    throw new Error('The report artifact service returned an invalid source or page identity.');
  }
  const sourceIdentity = parseSourceIdentity(candidate, sourceKind as ReportArtifactSourceKind);
  if (request && (
    candidate.reportId !== request.reportId
    || candidate.reportRevisionId !== request.reportRevisionId
    || candidate.format !== request.format
    || sourceKind !== (request.sourceKind ?? 'report')
    || candidate.view !== request.view
    || candidate.summaryBuilderVersion !== request.summaryBuilderVersion
    || (request.pdfBuilderVersion !== undefined && candidate.pdfBuilderVersion !== request.pdfBuilderVersion)
    || !sameSourceIdentity(sourceIdentity, request)
    || (
      request.format === 'pdf'
      && candidate.pageSize !== request.pageSize
    )
  )) {
    throw new Error('The report artifact job identity does not match this immutable revision.');
  }
  const artifact = parseArtifact(candidate.artifact);
  if (artifact && artifact.format !== candidate.format) {
    throw new Error('The report artifact format does not match its job.');
  }
  const status = candidate.status as ReportArtifactJobStatus;
  if (status === 'succeeded' && (
    candidate.artifactState !== 'ready'
    || Number(candidate.progress) !== 100
    || !artifact
  )) {
    throw new Error('The report artifact service returned an incomplete successful job.');
  }
  if (status === 'failed' && candidate.artifactState !== 'failed') {
    throw new Error('The report artifact service returned an invalid failed job.');
  }
  const deliveryNotification = record(candidate.deliveryNotification);
  const notificationStatus = deliveryNotification?.status;
  const notificationReason = deliveryNotification?.reason;
  if (deliveryNotification && (!['email-scheduled', 'email-sent', 'in-app-only', 'unavailable'].includes(String(notificationStatus))
    || (notificationReason !== undefined && !['email-unverified', 'email-unavailable', 'registration-failed'].includes(String(notificationReason))))) {
    throw new Error('The PDF notification subscription status is invalid.');
  }
  return {
    ...(deliveryNotification ? { deliveryNotification: {
      status: notificationStatus as ReportArtifactDeliveryNotification['status'],
      ...(notificationReason ? { reason: notificationReason as ReportArtifactDeliveryNotification['reason'] } : {}),
    } } : {}),
    jobId: candidate.jobId,
    reportId: candidate.reportId,
    reportRevisionId: candidate.reportRevisionId,
    sourceKind: sourceKind as ReportArtifactSourceKind,
    ...sourceIdentity,
    ...((candidate.view === 'screening-summary' || candidate.view === 'report-summary') ? { view: candidate.view } : {}),
    ...(typeof candidate.summaryBuilderVersion === 'string' ? { summaryBuilderVersion: candidate.summaryBuilderVersion } : {}),
    ...(typeof candidate.pdfBuilderVersion === 'string' ? { pdfBuilderVersion: candidate.pdfBuilderVersion } : {}),
    format: candidate.format as ReportArtifactFormat,
    ...(candidate.format === 'pdf'
      ? { pageSize: candidate.pageSize as ReportArtifactPageSize }
      : {}),
    requestHash: candidate.requestHash,
    status,
    attempt: Number(candidate.attempt),
    progress: Number(candidate.progress),
    artifactState: candidate.artifactState as ReportArtifactJobView['artifactState'],
    createdAt: candidate.createdAt,
    updatedAt: candidate.updatedAt,
    ...(optionalString(candidate.errorCode) ? { errorCode: optionalString(candidate.errorCode) } : {}),
    ...(optionalString(candidate.error) ? { error: optionalString(candidate.error) } : {}),
    ...(optionalString(candidate.contentUrl) ? { contentUrl: optionalString(candidate.contentUrl) } : {}),
    ...(artifact ? { artifact } : {}),
  };
}

async function responseBody(response: Response): Promise<unknown> {
  const raw = await response.text();
  if (!raw) return {};
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return { error: raw };
  }
}

function errorMessage(body: unknown, fallback: string): string {
  const candidate = record(body);
  return optionalString(candidate?.error) ?? optionalString(candidate?.detail) ?? fallback;
}

function abortError(message = 'Report artifact generation was cancelled.'): Error {
  const error = new Error(message);
  error.name = 'AbortError';
  return error;
}

async function delay(ms: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) throw abortError();
  await new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = (): void => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
      reject(abortError());
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

export async function enqueueArtifactJob(
  request: ReportArtifactJobRequest,
  fetcher: typeof authedFetch = authedFetch,
  signal?: AbortSignal,
  ownerQuery?: string,
): Promise<ReportArtifactJobView> {
  const response = await fetcher(`${ROUTE_BASE}${ownerQuery ?? ""}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(request),
    signal,
  });
  const body = await responseBody(response);
  if (!response.ok) {
    throw new Error(errorMessage(body, `Report artifact request failed (${response.status}).`));
  }
  return parseJob(body, request);
}

async function readArtifactJob(
  jobId: string,
  request: ReportArtifactJobRequest,
  fetcher: typeof authedFetch,
  signal?: AbortSignal,
  ownerQuery?: string,
): Promise<ReportArtifactJobView> {
  const response = await fetcher(`${ROUTE_BASE}/${encodeURIComponent(jobId)}${ownerQuery ?? ""}`, { signal });
  const body = await responseBody(response);
  if (!response.ok) {
    throw new Error(errorMessage(body, `Report artifact status failed (${response.status}).`));
  }
  const job = parseJob(body, request);
  if (job.jobId !== jobId) throw new Error('The report artifact status returned the wrong job.');
  return job;
}

/**
 * Build one immutable delivery projection and return the exact authenticated
 * bytes committed for that report revision. Client DOM state is never used.
 */
export async function buildReportArtifact(
  request: ReportArtifactJobRequest,
  options: BuildReportArtifactOptions = {},
): Promise<BuiltReportArtifact> {
  const ownerUid = options.adminReportOwnerUid ?? options.reportOwnerUid;
  if ((options.adminReportOwnerUid !== undefined && options.reportOwnerUid !== undefined)
    || (ownerUid !== undefined && (!/^[A-Za-z0-9_-]{1,128}$/.test(ownerUid) || request.format !== 'pdf'))) {
    throw new Error('PDF access requires one valid owner and a PDF request.');
  }
  const ownerQuery = ownerUid === undefined ? '' : `?${options.adminReportOwnerUid !== undefined ? 'adminReportOwnerUid' : 'reportOwnerUid'}=${encodeURIComponent(ownerUid)}`;
  const fetcher = options.fetcher ?? authedFetch;
  const now = options.now ?? Date.now;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const pollIntervalMs = options.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const startedAt = now();
  let job = await enqueueArtifactJob(request, fetcher, options.signal, ownerQuery);
  options.onProgress?.(job);
  while (job.status === 'queued' || job.status === 'running') {
    if (options.signal?.aborted) throw abortError();
    if (now() - startedAt >= timeoutMs) {
      throw new Error('Report artifact generation timed out. The durable job may still complete; retry shortly.');
    }
    await delay(pollIntervalMs, options.signal);
    job = await readArtifactJob(job.jobId, request, fetcher, options.signal, ownerQuery);
    options.onProgress?.(job);
  }
  if (job.status === 'failed') {
    throw new Error(job.error || `Report artifact generation failed${job.errorCode ? ` (${job.errorCode})` : ''}.`);
  }
  if (!job.artifact) throw new Error('The successful report artifact job has no immutable artifact.');
  const contentUrl = `${ROUTE_BASE}/${encodeURIComponent(job.jobId)}/content`;
  const response = await fetcher(`${contentUrl}${ownerQuery}`, { signal: options.signal });
  if (!response.ok) {
    const body = await responseBody(response);
    throw new Error(errorMessage(body, `Report artifact download failed (${response.status}).`));
  }
  const expectedEtag = `"sha256-${job.artifact.sha256}"`;
  if (response.headers.get('etag') !== expectedEtag) {
    throw new Error('The downloaded report artifact ETag does not match its committed SHA-256 identity.');
  }
  const blob = await response.blob();
  if (blob.size !== job.artifact.byteLength) {
    throw new Error('The downloaded report artifact does not match its committed byte length.');
  }
  if (blob.type && blob.type !== job.artifact.mediaType) {
    throw new Error('The downloaded report artifact media type does not match its manifest.');
  }
  return { job, blob };
}
